import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import US_STATES from '@careerstart/wae-common/src/main/constants/US_STATES';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { validateAddress } from '@careerstart/wae-common/src/main/helperFunction/profileHelperss';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import {
  selectAgencyDetail,
  selectIsAgencyLoading,
  selectIsUpdatingAgency,
} from '../../../../store/selectors/dashboardSelector';
import { BLACK, PRIMARY_PURPLE } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import {
  getAgencyForAgencyDetail,
  updateAgencyAddress,
  updateAgencyIndustries,
} from '../../dashboardReducer';
import IndustriesCard from '../components/IndustryPickerCard';
import { AGENCY_ADDRESS_UPDATE_FIELDS, AGENCY_INDUSTRY_UPDATE_FIELDS } from '../constant';

const ADDRESS_REQUIRED_FIELDS = [
  AGENCY_ADDRESS_UPDATE_FIELDS.ADDRESS1,
  AGENCY_ADDRESS_UPDATE_FIELDS.CITY,
  AGENCY_ADDRESS_UPDATE_FIELDS.STATE,
  AGENCY_ADDRESS_UPDATE_FIELDS.ZIP,
];
const INDUSTRY_REQUIRED_FIELDS = [AGENCY_INDUSTRY_UPDATE_FIELDS.INDUSTRIES];

const inputStyle = {
  width: '300px',
  '& .MuiInputLabel-root': {
    color: BLACK[100],
    fontFamily: PRIMARY_FONT[400],
    marginBottom: '8px',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: PRIMARY_PURPLE[70],
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: BLACK[100],
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(14px, -9px) scale(0.75)',
  },
  '& .MuiOutlinedInput-root': {
    marginTop: '8px',
  },
};

const validateZipCode = (zipcode) => {
  const basicZipRegex = /^\d{5}$/;
  const zipPlusFourRegex = /^\d{5}-\d{4}$/;

  if (!zipcode) return 'dashboard.agency.create.required';
  if (!basicZipRegex.test(zipcode) && !zipPlusFourRegex.test(zipcode)) {
    return 'dashboard.agency.create.zip.validate';
  }
  return null;
};

const AgencyDetailEditPage = () => {
  const agencyDetail = useSelector(selectAgencyDetail);

  const addressData = {
    address1: agencyDetail?.address?.address1 || '',
    address2: agencyDetail?.address?.address2 || '',
    city: agencyDetail?.address?.city || '',
    state: agencyDetail?.address?.state?.toUpperCase() || '',
    zipcode: agencyDetail?.address?.zipcode || '',
  };

  const [addressFormData, setAddressFormData] = useState(addressData);

  const [industryFormData, setIndustryFormData] = useState(agencyDetail?.industries);

  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const { id } = useParams();
  const isUpdatingAgency = useSelector(selectIsUpdatingAgency);
  const isGetAgencyLoading = useSelector(selectIsAgencyLoading);

  const validateAddressForm = () => {
    const newErrors = {};

    const isEmpty = (value) => {
      if (Array.isArray(value)) {
        return value.length === 0;
      }

      return (
        value === null ||
        value === undefined ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '')
      );
    };

    ADDRESS_REQUIRED_FIELDS?.forEach((field) => {
      if (isEmpty(addressFormData[field])) {
        newErrors[field] = 'dashboard.agency.create.required';
      }
    });

    const addressValidationResult = validateAddress({
      address1: addressFormData.address1,
      address2: addressFormData.address2,
      city: addressFormData.city,
      state: addressFormData.state,
      zipcode: addressFormData.zipcode,
    });

    if (!addressValidationResult.isValid) {
      Object.entries(addressValidationResult.errors || {}).forEach(([field, error]) => {
        if (error) {
          newErrors[field] = error;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;

    if (field === AGENCY_ADDRESS_UPDATE_FIELDS.ZIP) {
      const zipError = validateZipCode(value);
      setErrors((prev) => ({
        ...prev,
        [field]: zipError && zipError,
      }));
    } else if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    setAddressFormData((prev) => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value,
          },
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };
  const handleAddressSubmit = (e) => {
    e.preventDefault();
    const isValid = validateAddressForm();

    if (isValid) {
      dispatch(
        updateAgencyAddress({
          agencyId: agencyDetail?._id,
          address: addressFormData,
        })
      );
    }
  };

  const validateIndustryForm = () => {
    const newErrors = {};

    const isEmpty = (value) => {
      if (Array.isArray(value)) {
        return value.length === 0;
      }

      return (
        value === null ||
        value === undefined ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '')
      );
    };

    INDUSTRY_REQUIRED_FIELDS?.forEach((field) => {
      if (isEmpty(industryFormData)) {
        newErrors[field] = 'dashboard.agency.create.required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleIndustrySubmit = (e) => {
    e.preventDefault();
    const isValid = validateIndustryForm();

    if (isValid) {
      dispatch(
        updateAgencyIndustries({
          agencyId: agencyDetail?._id,
          industries: industryFormData,
        })
      );
    }
  };
  const getError = (field) => errors[field];

  const getHelperText = (field) => {
    const error = getError(field);
    return error ? LanguageConverterV2(error) : '';
  };

  useEffect(() => {
    const payload = {
      filters: [
        {
          operation: 'equalsID',
          field: '_id',
          value: id,
        },
      ],
    };
    dispatch(getAgencyForAgencyDetail(payload));
  }, [id, dispatch]);

  useEffect(() => {
    const newAddressData = {
      address1: agencyDetail?.address?.address1 || '',
      address2: agencyDetail?.address?.address2 || '',
      city: agencyDetail?.address?.city || '',
      state: agencyDetail?.address?.state?.toUpperCase() || '',
      zipcode: agencyDetail?.address?.zipcode || '',
    };
    setAddressFormData(newAddressData);

    setIndustryFormData(agencyDetail?.industries || []);
  }, [agencyDetail]);

  return isGetAgencyLoading ? (
    <CircularProgress />
  ) : (
    <Box
      sx={{
        pt: '24px',
      }}
    >
      <Typography sx={{ fontFamily: PRIMARY_FONT[700], fontSize: '20px', pb: '16px' }}>
        {LanguageConverterV2('dashboard.agency.edit.agencyDetails')}
      </Typography>
      <Box sx={{ pb: '8px' }}>
        <TextField
          required
          label={LanguageConverterV2('dashboard.agency.create.addressLine1')}
          value={addressFormData.address1}
          onChange={handleChange(AGENCY_ADDRESS_UPDATE_FIELDS.ADDRESS1)}
          fullWidth
          sx={inputStyle}
          error={!!getError(AGENCY_ADDRESS_UPDATE_FIELDS.ADDRESS1)}
          helperText={
            getHelperText(AGENCY_ADDRESS_UPDATE_FIELDS.ADDRESS1) ||
            LanguageConverterV2('dashboard.agency.create.noPO')
          }
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <TextField
          sx={inputStyle}
          label={LanguageConverterV2('dashboard.agency.create.addressLine2')}
          value={addressFormData.address2}
          onChange={handleChange(AGENCY_ADDRESS_UPDATE_FIELDS.ADDRESS2)}
          fullWidth
          helperText={getHelperText(AGENCY_ADDRESS_UPDATE_FIELDS.ADDRESS2)}
        />

        <TextField
          required
          label={LanguageConverterV2('dashboard.agency.create.city')}
          value={addressFormData.city}
          onChange={handleChange(AGENCY_ADDRESS_UPDATE_FIELDS.CITY)}
          sx={{ ...inputStyle, width: '160px' }}
          error={getError(AGENCY_ADDRESS_UPDATE_FIELDS.CITY)}
          helperText={getHelperText(AGENCY_ADDRESS_UPDATE_FIELDS.CITY)}
        />

        <FormControl
          error={!!getError(AGENCY_ADDRESS_UPDATE_FIELDS.STATE)}
          required
          sx={{ ...inputStyle, width: '100px' }}
        >
          <InputLabel id="state-select-label">
            {LanguageConverterV2('dashboard.agency.create.state')}
          </InputLabel>
          <Select
            data-testid="state-select"
            labelId="state-select-label"
            value={addressFormData.state}
            onChange={handleChange(AGENCY_ADDRESS_UPDATE_FIELDS.STATE)}
            label={LanguageConverterV2('dashboard.agency.create.state')}
          >
            {US_STATES?.map((st) => (
              <MenuItem key={st.value} value={st.value}>
                {st.label}
              </MenuItem>
            ))}
          </Select>
          {!!getError(AGENCY_ADDRESS_UPDATE_FIELDS.STATE) && (
            <FormHelperText>{getHelperText(AGENCY_ADDRESS_UPDATE_FIELDS.STATE)}</FormHelperText>
          )}
        </FormControl>
        <TextField
          required
          label={LanguageConverterV2('dashboard.agency.create.zip.placeholder')}
          value={addressFormData.zipcode}
          onChange={handleChange(AGENCY_ADDRESS_UPDATE_FIELDS.ZIP)}
          error={!!getError(AGENCY_ADDRESS_UPDATE_FIELDS.ZIP)}
          helperText={getHelperText(AGENCY_ADDRESS_UPDATE_FIELDS.ZIP)}
          sx={{ ...inputStyle, width: '200px' }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 4,
            pl: 36,
          }}
        >
          {isUpdatingAgency ? (
            <CircularProgress />
          ) : (
            <Button
              variant="contained"
              onClick={handleAddressSubmit}
              sx={{
                fontFamily: PRIMARY_FONT[700],
                fontSize: '16px',
                textTransform: 'none',
              }}
            >
              {LanguageConverterV2('dashboard.agency.edit.updateAgency')}
            </Button>
          )}
        </Box>
        <IndustriesCard
          selectedIndustries={industryFormData}
          error={errors.industries}
          onChange={(newIndustries) => setIndustryFormData(newIndustries)}
          required
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 4,
            pl: 36,
          }}
        >
          {isUpdatingAgency ? (
            <CircularProgress />
          ) : (
            <Button
              variant="contained"
              onClick={handleIndustrySubmit}
              sx={{
                fontFamily: PRIMARY_FONT[700],
                fontSize: '16px',
                textTransform: 'none',
              }}
            >
              {LanguageConverterV2('dashboard.agency.edit.updateAgency')}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AgencyDetailEditPage;
